// SEO Configuration for all pages
// Implements proper meta tags, schema markup, and GEO optimization

export interface SEOConfig {
  title: string;
  description: string;
  keywords: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  schema?: Record<string, any>;
  canonical?: string;
}

// Base configuration for all pages
const baseConfig = {
  siteName: '3 Arrows Media',
  siteUrl: 'https://3arrowsmedia.com',
  logoUrl: 'https://3arrowsmedia.com/logo.png',
  company: {
    name: '3 Arrows Media',
    description: 'Professional Digital Marketing Agency specializing in lead generation, web development, and digital advertising.',
    email: 'info@3arrowsmedia.com',
    phone: '+9197118 34001',
    address: {
      streetAddress: '123 Business Avenue',
      addressLocality: 'New York',
      addressRegion: 'NY',
      postalCode: '10001',
      addressCountry: 'US'
    },
    sameAs: [
      'https://facebook.com/3arrowsmedia',
      'https://twitter.com/3arrowsmedia',
      'https://linkedin.com/company/3arrowsmedia',
      'https://instagram.com/3arrowsmedia'
    ]
  }
};

// Page-specific SEO configurations
export const pageConfigs: Record<string, SEOConfig> = {
  home: {
    title: '3 Arrows Media - Digital Marketing Excellence | Local & E-commerce Solutions',
    description: 'Transform your business with our comprehensive digital marketing solutions. Lead generation, web development, mobile apps, social media, and advertising services for local and e-commerce businesses.',
    keywords: [
      'digital marketing agency',
      'lead generation',
      'web development',
      'mobile app development',
      'social media management',
      'google ads management',
      'meta ads',
      'amazon advertising',
      'local SEO',
      'digital marketing services'
    ],
    ogTitle: '3 Arrows Media - Digital Marketing Excellence',
    ogDescription: 'Professional digital marketing services including lead generation, web development, and advertising management.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: baseConfig.company.name,
      description: baseConfig.company.description,
      url: baseConfig.siteUrl,
      email: baseConfig.company.email,
      telephone: baseConfig.company.phone,
      logo: baseConfig.logoUrl,
      address: {
        '@type': 'PostalAddress',
        ...baseConfig.company.address
      },
      sameAs: baseConfig.company.sameAs,
      knowsAbout: [
        'Digital Marketing',
        'Lead Generation',
        'Web Development',
        'Mobile App Development',
        'Social Media Management',
        'Google Ads',
        'Meta Ads',
        'Amazon Advertising',
        'Local SEO'
      ]
    }
  },

  leadGeneration: {
    title: 'Lead Generation for Local Business | 3 Arrows Media',
    description: 'Strategic lead generation services for local businesses. Drive qualified leads through local SEO, Google My Business optimization, and targeted PPC campaigns.',
    keywords: [
      'lead generation',
      'local lead generation',
      'local SEO',
      'google my business',
      'local business',
      'qualified leads',
      'local PPC',
      'review management',
      'local search optimization'
    ],
    ogTitle: 'Lead Generation Services for Local Business',
    ogDescription: 'Drive qualified local leads with our proven lead generation strategies.',
    ogType: 'service',
    ogUrl: `${baseConfig.siteUrl}/lead-generation`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Lead Generation for Local Business',
      description: 'Strategic lead generation services tailored for local businesses to drive qualified customer acquisitions.',
      provider: {
        '@type': 'Organization',
        name: baseConfig.company.name,
        telephone: baseConfig.company.phone,
        email: baseConfig.company.email
      },
      areaServed: {
        '@type': 'Country',
        name: 'US'
      },
      hasOfferingDetails: [
        {
          '@type': 'OfferingDetails',
          name: 'Local SEO Optimization'
        },
        {
          '@type': 'OfferingDetails',
          name: 'Google My Business Management'
        },
        {
          '@type': 'OfferingDetails',
          name: 'Local PPC Campaigns'
        },
        {
          '@type': 'OfferingDetails',
          name: 'Review Management'
        }
      ]
    }
  },

  mobileDevelopment: {
    title: 'Mobile App Development Services | iOS & Android Apps | 3 Arrows Media',
    description: 'Custom mobile app development for iOS and Android. Native and cross-platform applications built to drive business growth.',
    keywords: [
      'mobile app development',
      'iOS app development',
      'android app development',
      'cross-platform development',
      'app development services',
      'custom mobile apps',
      'react native',
      'mobile solution'
    ],
    ogTitle: 'Professional Mobile App Development',
    ogDescription: 'Custom iOS and Android app development tailored to your business needs.',
    ogType: 'service',
    ogUrl: `${baseConfig.siteUrl}/mobile-development`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Mobile App Development',
      description: 'Custom mobile application development for iOS and Android platforms.',
      provider: {
        '@type': 'Organization',
        name: baseConfig.company.name,
        telephone: baseConfig.company.phone
      },
      areaServed: {
        '@type': 'Country',
        name: 'US'
      }
    }
  },

  websiteDevelopment: {
    title: 'Website Development Services | Conversion-Focused Web Design | 3 Arrows Media',
    description: 'Custom website development focused on conversions. Responsive, fast, and SEO-optimized websites built to grow your business.',
    keywords: [
      'website development',
      'web design',
      'website design',
      'responsive website',
      'e-commerce website',
      'custom website',
      'conversion optimization',
      'web development services'
    ],
    ogTitle: 'Professional Website Development',
    ogDescription: 'Custom websites designed for conversions and SEO performance.',
    ogType: 'service',
    ogUrl: `${baseConfig.siteUrl}/website-development`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Website Development',
      description: 'Custom website development with focus on conversion optimization and SEO.',
      provider: {
        '@type': 'Organization',
        name: baseConfig.company.name,
        telephone: baseConfig.company.phone
      },
      areaServed: {
        '@type': 'Country',
        name: 'US'
      }
    }
  },

  digitalMarketing: {
    title: 'Digital Marketing Services | Complete Marketing Strategy | 3 Arrows Media',
    description: 'Comprehensive digital marketing strategies combining SEO, content marketing, social media, and paid advertising for measurable results.',
    keywords: [
      'digital marketing',
      'digital marketing services',
      'marketing strategy',
      'content marketing',
      'SEO services',
      'online marketing',
      'digital strategy',
      'marketing agency'
    ],
    ogTitle: 'Digital Marketing Services',
    ogDescription: 'Comprehensive digital marketing strategies for business growth.',
    ogType: 'service',
    ogUrl: `${baseConfig.siteUrl}/digital-marketing`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Digital Marketing',
      description: 'Comprehensive digital marketing services including SEO, content marketing, and paid advertising.',
      provider: {
        '@type': 'Organization',
        name: baseConfig.company.name,
        telephone: baseConfig.company.phone
      }
    }
  },

  socialMedia: {
    title: 'Social Media Management Services | Facebook & Instagram Marketing | 3 Arrows Media',
    description: 'Professional social media management for Facebook, Instagram, LinkedIn, and TikTok. Content creation, community management, and paid social campaigns.',
    keywords: [
      'social media management',
      'facebook marketing',
      'instagram marketing',
      'social media marketing',
      'content creation',
      'community management',
      'social media strategy',
      'paid social advertising'
    ],
    ogTitle: 'Social Media Management & Marketing',
    ogDescription: 'Professional social media management across all major platforms.',
    ogType: 'service',
    ogUrl: `${baseConfig.siteUrl}/social-media`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Social Media Management',
      description: 'Professional social media management and marketing services.',
      provider: {
        '@type': 'Organization',
        name: baseConfig.company.name,
        telephone: baseConfig.company.phone
      }
    }
  },

  metaAds: {
    title: 'Meta Ads Management | Facebook & Instagram Advertising | 3 Arrows Media',
    description: 'Expert Meta (Facebook & Instagram) ads management. Data-driven campaigns optimized for ROI and conversions.',
    keywords: [
      'meta ads',
      'facebook ads',
      'instagram ads',
      'facebook advertising',
      'ads management',
      'paid social',
      'conversion ads',
      'meta advertising'
    ],
    ogTitle: 'Meta Ads Management & Advertising',
    ogDescription: 'Expert Facebook and Instagram ads management for maximum ROI.',
    ogType: 'service',
    ogUrl: `${baseConfig.siteUrl}/meta-ads`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Meta Ads Management',
      description: 'Professional Meta (Facebook & Instagram) advertising and campaign management.',
      provider: {
        '@type': 'Organization',
        name: baseConfig.company.name,
        telephone: baseConfig.company.phone
      }
    }
  },

  googleAds: {
    title: 'Google Ads Management | PPC & Search Advertising | 3 Arrows Media',
    description: 'Expert Google Ads management including Search, Display, Shopping, and YouTube ads. Maximize ROI with data-driven campaigns.',
    keywords: [
      'google ads',
      'google adwords',
      'PPC advertising',
      'search ads',
      'google shopping',
      'display ads',
      'youtube ads',
      'search engine marketing'
    ],
    ogTitle: 'Google Ads Management & PPC Services',
    ogDescription: 'Expert Google Ads management across all platforms for maximum conversions.',
    ogType: 'service',
    ogUrl: `${baseConfig.siteUrl}/google-ads`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Google Account Management',
      description: 'Professional Google Ads management including Search, Display, Shopping, and YouTube advertising.',
      provider: {
        '@type': 'Organization',
        name: baseConfig.company.name,
        telephone: baseConfig.company.phone
      }
    }
  },

  amazonAds: {
    title: 'Amazon Advertising Services | Marketplace Management | 3 Arrows Media',
    description: 'Amazon ads management and marketplace optimization. Drive product visibility and sales on Amazon with expert campaign management.',
    keywords: [
      'amazon ads',
      'amazon advertising',
      'amazon PPC',
      'marketplace advertising',
      'product advertising',
      'amazon seller',
      'sponsored products',
      'amazon marketing'
    ],
    ogTitle: 'Amazon Advertising Services',
    ogDescription: 'Expert Amazon ads management to increase product visibility and sales.',
    ogType: 'service',
    ogUrl: `${baseConfig.siteUrl}/amazon-ads`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Amazon Advertising Services',
      description: 'Professional Amazon ads management and marketplace optimization services.',
      provider: {
        '@type': 'Organization',
        name: baseConfig.company.name,
        telephone: baseConfig.company.phone
      }
    }
  },

  about: {
    title: 'About 3 Arrows Media | Digital Marketing Agency',
    description: 'Learn about 3 Arrows Media. Our mission, values, team expertise, and proven track record in digital marketing excellence.',
    keywords: [
      'about us',
      '3 arrows media',
      'digital marketing agency',
      'about agency',
      'team',
      'company mission',
      'company values'
    ],
    ogTitle: 'About 3 Arrows Media',
    ogDescription: 'Discover our mission, values, and expertise in digital marketing.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/about`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About 3 Arrows Media',
      description: 'Learn about our digital marketing agency, mission, values, and expertise.'
    }
  },

  contact: {
    title: 'Contact 3 Arrows Media | Get in Touch',
    description: 'Contact 3 Arrows Media. Call, email, or fill out a form to discuss your digital marketing needs with our team.',
    keywords: [
      'contact us',
      'contact',
      'get in touch',
      'contact form',
      'phone number',
      'email address',
      'business contact'
    ],
    ogTitle: 'Contact 3 Arrows Media',
    ogDescription: 'Get in touch with our digital marketing team.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/contact`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact 3 Arrows Media',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Service',
        telephone: baseConfig.company.phone,
        email: baseConfig.company.email
      }
    }
  },

  consultation: {
    title: 'Schedule a Consultation | Free Strategy Session | 3 Arrows Media',
    description: 'Schedule a free consultation with our digital marketing experts. Discuss your business goals and get a personalized strategy.',
    keywords: [
      'schedule consultation',
      'consultation',
      'free consultation',
      'strategy session',
      'business consultation',
      'digital marketing consultation'
    ],
    ogTitle: 'Schedule Your Free Consultation',
    ogDescription: 'Book a free consultation with our digital marketing experts.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/consultation`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: baseConfig.company.name,
      telephone: baseConfig.company.phone,
      email: baseConfig.company.email,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Service',
        telephone: baseConfig.company.phone
      }
    }
  },

  portfolio: {
    title: 'Portfolio | Case Studies & Success Stories | 3 Arrows Media',
    description: 'View our portfolio of successful projects and case studies showcasing our digital marketing expertise.',
    keywords: [
      'portfolio',
      'case studies',
      'projects',
      'success stories',
      'client work',
      'examples'
    ],
    ogTitle: 'Portfolio & Case Studies',
    ogDescription: 'See our successful projects and client success stories.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/portfolio`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Portfolio',
      description: 'Portfolio of successful digital marketing projects.'
    }
  },

  quote: {
    title: 'Get a Free Project Quote | 3 Arrows Media',
    description: 'Request a detailed quote for your digital marketing project. Tell us about your needs and get a customized proposal.',
    keywords: [
      'project quote',
      'free quote',
      'estimate',
      'pricing',
      'project pricing',
      'digital marketing quote',
      'web development quote',
      'mobile app quote'
    ],
    ogTitle: 'Get Your Free Project Quote',
    ogDescription: 'Request a detailed quote for your digital marketing project.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/quote`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Project Quote Request',
      description: 'Get a free quote for your digital marketing project'
    }
  },

  blog: {
    title: 'Digital Marketing Blog | Tips, Insights & Strategies | 3 Arrows Media',
    description: 'Read our latest articles on digital marketing, lead generation, web development, and advertising strategies for your business.',
    keywords: [
      'digital marketing blog',
      'marketing tips',
      'digital marketing strategies',
      'lead generation tips',
      'web development insights',
      'marketing insights',
      'business growth',
      'digital marketing advice'
    ],
    ogTitle: 'Digital Marketing Insights & Blog',
    ogDescription: 'Read expert tips and strategies on digital marketing, lead generation, and business growth.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/blog`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: '3 Arrows Media Blog',
      description: 'Digital marketing insights and strategies blog'
    }
  },

  industries: {
    title: 'Industries We Serve | Vertical Expertise | 3 Arrows Media',
    description: 'Explore the industries we specialize in. We provide tailored digital marketing solutions for healthcare, e-commerce, real estate, finance, and more.',
    keywords: [
      'industries',
      'industry solutions',
      'vertical expertise',
      'healthcare marketing',
      'real estate marketing',
      'e-commerce marketing',
      'financial services marketing',
      'industry-specific solutions'
    ],
    ogTitle: 'Industries We Serve',
    ogDescription: 'Discover our expertise across healthcare, e-commerce, real estate, and other key industries.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/industries`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Industries We Serve',
      description: 'Industries and vertical markets served by 3 Arrows Media'
    }
  },

  caseStudies: {
    title: 'Case Studies | Success Stories & Results | 3 Arrows Media',
    description: 'Read detailed case studies showing how we helped businesses achieve their goals through digital marketing, web development, and advertising strategies.',
    keywords: [
      'case studies',
      'success stories',
      'client results',
      'project results',
      'before and after',
      'business case studies',
      'marketing results',
      'ROI examples'
    ],
    ogTitle: 'Case Studies & Success Stories',
    ogDescription: 'See the measurable results we achieved for our clients across various industries.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/case-studies`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Case Studies',
      description: 'Collection of client success stories and case studies'
    }
  },

  privacyPolicy: {
    title: 'Privacy Policy | 3 Arrows Media',
    description: 'Read our privacy policy to understand how we collect, use, and protect your personal information.',
    keywords: [
      'privacy policy',
      'data privacy',
      'privacy notice',
      'personal data',
      'data collection',
      'privacy terms'
    ],
    ogTitle: 'Privacy Policy',
    ogDescription: 'Learn how we protect your privacy and personal information.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/privacy-policy`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy',
      description: 'Privacy policy for 3 Arrows Media'
    }
  },

  termsOfService: {
    title: 'Terms of Service | 3 Arrows Media',
    description: 'Review our terms of service governing your use of our website and services.',
    keywords: [
      'terms of service',
      'terms and conditions',
      'legal terms',
      'usage terms',
      'service terms'
    ],
    ogTitle: 'Terms of Service',
    ogDescription: 'Our terms of service and conditions for using our website.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/terms-of-service`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service',
      description: 'Terms of service for 3 Arrows Media website and services'
    }
  },

  dataProtection: {
    title: 'Data Protection Policy | 3 Arrows Media',
    description: 'Our data protection policy explains how we comply with data protection regulations including GDPR and CCPA.',
    keywords: [
      'data protection',
      'GDPR compliance',
      'CCPA compliance',
      'data privacy',
      'data security',
      'data protection policy',
      'privacy compliance'
    ],
    ogTitle: 'Data Protection Policy',
    ogDescription: 'Learn about our data protection practices and regulatory compliance.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/data-protection`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Data Protection Policy',
      description: 'Data protection policy and privacy compliance information'
    }
  },

  accessibility: {
    title: 'Accessibility Statement | 3 Arrows Media',
    description: 'Our commitment to web accessibility and inclusive design for all users.',
    keywords: [
      'accessibility',
      'web accessibility',
      'WCAG',
      'accessible design',
      'inclusive design',
      'accessibility statement',
      'disability access'
    ],
    ogTitle: 'Accessibility Statement',
    ogDescription: 'Learn about our accessibility features and commitment to inclusive design.',
    ogType: 'website',
    ogUrl: `${baseConfig.siteUrl}/accessibility`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Accessibility Statement',
      description: 'Accessibility statement and commitment to web accessibility'
    }
  }
};

export { baseConfig };